import { FormEvent, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "shared/services/api";
import {
  buscarVariacaoGlobal,
  criarVariacaoGlobal,
  desativarVariacaoGlobal,
  listarVariacoesGlobais,
  listarValoresVariacaoGlobal,
  sincronizarVariacaoGlobal,
  type CreateGlobalVariationData,
  type GlobalVariation,
  type SyncGlobalVariationData,
  type VariationType,
} from "shared/services/api/variacoes.api";
import {
  MAX_VARIATION_DECIMAL_PLACES,
  formatVariationValue,
  normalizeVariationValue,
  sanitizeNumberInput,
  type VariationValueRules,
} from "shared/utils/variationValue";
import styles from "./VariacoesGlobais.module.scss";

interface CategoryOption {
  id: string;
  name: string;
}

interface SubcategoryOption {
  id: string;
  name: string;
}

/** A category the variation is offered in; no subcategory means all of them. */
interface CategoryBlock {
  categoryId: string;
  subcategoryIds: string[];
}

interface DraftValue {
  id?: string;
  label: string;
}

/**
 * Subcategory choice is hidden for now: a new category is offered whole.
 * Links to subcategories already saved are kept when the variation is saved.
 */
const SHOW_SUBCATEGORIES = false;

const DECIMAL_PLACE_OPTIONS = Array.from(
  { length: MAX_VARIATION_DECIMAL_PLACES + 1 },
  (_, index) => index,
);

/** Groups the saved links (one per category or subcategory) into blocks. */
function blocksFromLinks(
  links: Array<{ categoryId: string; subcategoryId?: string | null }>,
): CategoryBlock[] {
  const blocks = new Map<string, CategoryBlock>();
  const wholeCategories = new Set<string>();
  for (const link of links) {
    const block = blocks.get(link.categoryId) ?? {
      categoryId: link.categoryId,
      subcategoryIds: [],
    };
    blocks.set(link.categoryId, block);
    if (!link.subcategoryId) wholeCategories.add(link.categoryId);
    else if (!block.subcategoryIds.includes(link.subcategoryId)) {
      block.subcategoryIds.push(link.subcategoryId);
    }
  }
  // The whole category already covers every subcategory.
  return Array.from(blocks.values()).map((block) =>
    wholeCategories.has(block.categoryId)
      ? { ...block, subcategoryIds: [] }
      : block,
  );
}

type CategoryLink = { categoryId: string; subcategoryId: string | null };

function linksFromBlocks(blocks: CategoryBlock[]): CategoryLink[] {
  return blocks.flatMap((block): CategoryLink[] =>
    block.subcategoryIds.length > 0
      ? block.subcategoryIds.map((subcategoryId) => ({
          categoryId: block.categoryId,
          subcategoryId,
        }))
      : [{ categoryId: block.categoryId, subcategoryId: null }],
  );
}

function describeVariation(variation: GlobalVariation): string {
  if (variation.type !== "number") return "Texto";
  const parts = ["Número"];
  if (variation.unit) parts.push(variation.unit);
  parts.push(`${variation.decimalPlaces ?? 0} casa(s) decimal(is)`);
  return parts.join(" · ");
}

export const VariacoesGlobais = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const [variations, setVariations] = useState<GlobalVariation[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState<
    Record<string, SubcategoryOption[]>
  >({});
  const [values, setValues] = useState<DraftValue[]>([]);
  const [newValue, setNewValue] = useState("");
  const [valueError, setValueError] = useState("");
  const [categoryBlocks, setCategoryBlocks] = useState<CategoryBlock[]>([]);
  const [categoryToAdd, setCategoryToAdd] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<VariationType>("string");
  const [unit, setUnit] = useState("");
  const [decimalPlaces, setDecimalPlaces] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const isNumber = type === "number";
  const rules: VariationValueRules = { type, decimalPlaces };

  async function loadData() {
    setLoading(true);
    try {
      const [variationResponse, categoryResponse] = await Promise.all([
        listarVariacoesGlobais(),
        api.get<CategoryOption[]>("/categories"),
      ]);
      setVariations(variationResponse);
      setCategories(categoryResponse.data);
    } catch {
      setMessage("Não foi possível carregar os dados administrativos.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!id) return;
    void Promise.all([
      buscarVariacaoGlobal(id),
      listarValoresVariacaoGlobal(id),
    ])
      .then(([variation, existingValues]) => {
        setLabel(variation.label);
        setDescription(variation.description ?? "");
        setType(variation.type);
        setUnit(variation.unit ?? "");
        setDecimalPlaces(variation.decimalPlaces ?? 0);
        setCategoryBlocks(blocksFromLinks(variation.categories ?? []));
        setValues(
          existingValues.map((value) => ({
            id: value.id,
            label: value.label,
          })),
        );
      })
      .catch(() => setMessage("Não foi possível carregar a variação."));
  }, [id]);

  // Subcategory options, loaded once per category that gets a block.
  useEffect(() => {
    if (!SHOW_SUBCATEGORIES) return;
    const missing = categoryBlocks
      .map((block) => block.categoryId)
      .filter((categoryId) => !(categoryId in subcategoriesByCategory));
    for (const categoryId of missing) {
      setSubcategoriesByCategory((current) => ({ ...current, [categoryId]: [] }));
      void api
        .get<SubcategoryOption[]>(`/subcategories/${categoryId}`)
        .then((response) =>
          setSubcategoriesByCategory((current) => ({
            ...current,
            [categoryId]: response.data,
          })),
        )
        .catch(() => undefined);
    }
  }, [categoryBlocks, subcategoriesByCategory]);

  useEffect(() => {
    void loadData();
  }, []);

  function addCategory() {
    if (!categoryToAdd) return;
    setCategoryBlocks((current) =>
      current.some((block) => block.categoryId === categoryToAdd)
        ? current
        : [...current, { categoryId: categoryToAdd, subcategoryIds: [] }],
    );
    setCategoryToAdd("");
  }

  function removeCategory(categoryId: string) {
    setCategoryBlocks((current) =>
      current.filter((block) => block.categoryId !== categoryId),
    );
  }

  function changeSubcategories(categoryId: string, subcategoryIds: string[]) {
    setCategoryBlocks((current) =>
      current.map((block) =>
        block.categoryId === categoryId ? { ...block, subcategoryIds } : block,
      ),
    );
  }

  function categoryName(categoryId: string): string {
    return (
      categories.find((category) => category.id === categoryId)?.name ??
      "Categoria"
    );
  }

  function keyOf(itemLabel: string): string | undefined {
    const normalized = normalizeVariationValue(rules, itemLabel);
    return normalized.ok ? normalized.value : undefined;
  }

  /** Only while creating: a saved variation keeps its type. */
  function changeType(nextType: VariationType) {
    if (nextType === type) return;

    if (nextType === "number") {
      const nextRules = { type: nextType, decimalPlaces };
      const invalid = values.filter(
        (item) => !normalizeVariationValue(nextRules, item.label).ok,
      );
      if (
        invalid.length > 0 &&
        !window.confirm(
          `Os valores ${invalid
            .map((item) => `"${item.label}"`)
            .join(", ")} não são números e serão removidos. Continuar?`,
        )
      ) {
        return;
      }
      setValues((current) =>
        current.flatMap((item) => {
          const normalized = normalizeVariationValue(nextRules, item.label);
          return normalized.ok ? [{ ...item, label: normalized.label }] : [];
        }),
      );
      setNewValue((current) => sanitizeNumberInput(current));
    }

    setValueError("");
    setType(nextType);
  }

  /** Reformats the values that fit; the rest are flagged on save. */
  function changeDecimalPlaces(next: number) {
    setDecimalPlaces(next);
    const nextRules = { type, decimalPlaces: next };
    setValues((current) =>
      current.map((item) => {
        const normalized = normalizeVariationValue(nextRules, item.label);
        return normalized.ok ? { ...item, label: normalized.label } : item;
      }),
    );
  }

  function addValue() {
    const normalized = normalizeVariationValue(rules, newValue);
    if (!normalized.ok) {
      setValueError(normalized.error);
      return;
    }
    if (values.some((item) => keyOf(item.label) === normalized.value)) {
      setValueError("Esse valor já existe nesta variação.");
      return;
    }

    setValues((current) => [...current, { label: normalized.label }]);
    setNewValue("");
    setValueError("");
  }

  function removeValue(index: number) {
    setValues((current) =>
      current.filter((_, itemIndex) => itemIndex !== index),
    );
  }

  function updateValue(index: number, nextValue: string) {
    const text = isNumber ? sanitizeNumberInput(nextValue) : nextValue;
    setValues((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, label: text } : item,
      ),
    );
  }

  function moveValue(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= values.length) return;

    setValues((current) => {
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");

    if (!label.trim()) {
      setMessage("Informe o nome da variação.");
      return;
    }

    if (values.length === 0) {
      setMessage("Adicione pelo menos um valor.");
      return;
    }

    const finalValues: Array<{ id?: string; label: string; sortOrder: number }> =
      [];
    const keys = new Set<string>();
    for (const [index, item] of values.entries()) {
      const normalized = normalizeVariationValue(rules, item.label);
      if (!normalized.ok) {
        setMessage(`Valor "${item.label}": ${normalized.error}`);
        return;
      }
      if (keys.has(normalized.value)) {
        setMessage("Existem valores repetidos.");
        return;
      }
      keys.add(normalized.value);
      finalValues.push({ id: item.id, label: normalized.label, sortOrder: index });
    }

    const payload: CreateGlobalVariationData = {
      label: label.trim(),
      description: description.trim() || undefined,
      type,
      inputMode: "selector",
      unit: isNumber ? unit.trim() || null : null,
      decimalPlaces: isNumber ? decimalPlaces : null,
      values: finalValues.map(({ label: valueLabel, sortOrder }) => ({
        label: valueLabel,
        sortOrder,
      })),
      categories:
        categoryBlocks.length > 0 ? linksFromBlocks(categoryBlocks) : undefined,
    };

    setSaving(true);
    try {
      if (editing && id) {
        const syncPayload: SyncGlobalVariationData = {
          label: payload.label,
          description: payload.description,
          type: payload.type,
          inputMode: payload.inputMode,
          unit: payload.unit,
          decimalPlaces: payload.decimalPlaces,
          values: finalValues,
          // Always sent: an empty list takes the variation out of every
          // category.
          categories: linksFromBlocks(categoryBlocks),
        };

        await sincronizarVariacaoGlobal(id, syncPayload);

        navigate("/admin/v2/variacoes");
      } else {
        await criarVariacaoGlobal(payload);
      }
      setLabel("");
      setDescription("");
      setType("string");
      setUnit("");
      setDecimalPlaces(0);
      setValues([]);
      setCategoryBlocks([]);
      setCategoryToAdd("");
      setMessage(
        editing
          ? "Variação global atualizada com sucesso."
          : "Variação global cadastrada com sucesso.",
      );
      await loadData();
    } catch (error: any) {
      const detail = error?.response?.data?.message;
      setMessage(
        typeof detail === "string"
          ? `Não foi possível salvar a variação: ${detail}`
          : "Não foi possível salvar a variação global.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(variationId: string) {
    if (!window.confirm("Desativar esta variação global?")) return;
    try {
      await desativarVariacaoGlobal(variationId);
      setMessage("Variação global desativada.");
      await loadData();
    } catch {
      setMessage("Não foi possível desativar a variação.");
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <p className={styles.kicker}>Admin V2</p>
          <h2>Variações globais</h2>
          <p className={styles.subtitle}>
            Cadastre definições reutilizáveis para o catálogo.
          </p>
        </div>
        <span className={styles.badge}>Somente ADMIN</span>
      </header>

      {message && <p className={styles.message}>{message}</p>}

      <section className={styles.grid}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <h3>{editing ? "Editar variação" : "Nova variação"}</h3>

          <label>
            Nome da propriedade
            <small className={styles.helperText}>
              Nome usado pelo lojista para identificar e configurar esta
              propriedade.
            </small>
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Ex.: Cor"
            />
          </label>

          <label>
            Título
            <small className={styles.helperText}>
              Texto exibido para o cliente final no anúncio do produto.
            </small>
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Ex.: Escolha a cor"
            />
          </label>

          <label>
            Entrada
            <select value="selector" disabled>
              <option value="selector">Seletor</option>
            </select>
          </label>

          <div
            className={isNumber ? styles.threeColumns : styles.twoColumns}
          >
            <label>
              Tipo
              <select
                value={type}
                disabled={editing}
                onChange={(event) =>
                  changeType(event.target.value as VariationType)
                }
              >
                <option value="string">Texto</option>
                <option value="number">Número</option>
              </select>
            </label>

            {isNumber && (
              <>
                <label>
                  Unidade de medida
                  <input
                    value={unit}
                    maxLength={20}
                    onChange={(event) => setUnit(event.target.value)}
                    placeholder="Ex.: cm"
                  />
                </label>

                <label>
                  Casas decimais
                  <select
                    value={decimalPlaces}
                    onChange={(event) =>
                      changeDecimalPlaces(Number(event.target.value))
                    }
                  >
                    {DECIMAL_PLACE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}
          </div>
          {editing && (
            <small className={styles.helperText}>
              O tipo não pode ser alterado depois que a variação é salva.
            </small>
          )}

          <div>
            <span className={styles.label}>Categorias</span>
            <small className={styles.helperText}>
              A variação fica disponível nas categorias adicionadas.
              {SHOW_SUBCATEGORIES &&
                " Sem subcategoria marcada, vale para a categoria inteira."}
            </small>
            <div className={styles.valueEntry}>
              <select
                value={categoryToAdd}
                onChange={(event) => setCategoryToAdd(event.target.value)}
                aria-label="Categoria a adicionar"
              >
                <option value="">Selecione uma categoria</option>
                {categories
                  .filter(
                    (category) =>
                      !categoryBlocks.some(
                        (block) => block.categoryId === category.id,
                      ),
                  )
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={addCategory}
                disabled={!categoryToAdd}
                aria-label="Adicionar categoria"
              >
                <Plus size={18} />
              </button>
            </div>
            {categoryBlocks.length > 0 && (
              <div className={styles.categoryBlocks}>
                {categoryBlocks.map((block) => (
                  <div className={styles.categoryBlock} key={block.categoryId}>
                    <div className={styles.categoryBlockHeader}>
                      <strong>{categoryName(block.categoryId)}</strong>
                      <button
                        type="button"
                        onClick={() => removeCategory(block.categoryId)}
                        aria-label={`Remover ${categoryName(block.categoryId)}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    {SHOW_SUBCATEGORIES && (
                      <label>
                        Subcategorias
                        <select
                          multiple
                          value={block.subcategoryIds}
                          onChange={(event) =>
                            changeSubcategories(
                              block.categoryId,
                              Array.from(
                                event.target.selectedOptions,
                                (option) => option.value,
                              ),
                            )
                          }
                        >
                          {(subcategoriesByCategory[block.categoryId] ?? []).map(
                            (subcategory) => (
                              <option key={subcategory.id} value={subcategory.id}>
                                {subcategory.name}
                              </option>
                            ),
                          )}
                        </select>
                        <small className={styles.helperText}>
                          {block.subcategoryIds.length > 0
                            ? `${block.subcategoryIds.length} subcategoria(s) marcada(s).`
                            : "Categoria inteira."}
                        </small>
                      </label>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <span className={styles.label}>Valores</span>
            <div className={styles.valueEntry}>
              <input
                value={newValue}
                inputMode={isNumber ? "decimal" : undefined}
                onChange={(event) => {
                  setNewValue(
                    isNumber
                      ? sanitizeNumberInput(event.target.value)
                      : event.target.value,
                  );
                  setValueError("");
                }}
                placeholder={isNumber ? "Ex.: 10,5" : "Ex.: Azul"}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addValue();
                  }
                }}
              />
              {isNumber && unit.trim() && (
                <span className={styles.unitSuffix}>{unit.trim()}</span>
              )}
              <button
                type="button"
                onClick={addValue}
                aria-label="Adicionar valor"
              >
                <Plus size={18} />
              </button>
            </div>
            {valueError && <p className={styles.valueError}>{valueError}</p>}
            <div className={styles.values}>
              {values.map((item, index) => (
                <div
                  className={styles.value}
                  key={item.id ?? `${item.label}-${index}`}
                >
                  <div className={styles.valueReorder}>
                    <button
                      type="button"
                      onClick={() => moveValue(index, -1)}
                      disabled={index === 0}
                      aria-label={`Mover ${item.label} para cima`}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveValue(index, 1)}
                      disabled={index === values.length - 1}
                      aria-label={`Mover ${item.label} para baixo`}
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>
                  <input
                    value={item.label}
                    inputMode={isNumber ? "decimal" : undefined}
                    onChange={(event) => updateValue(index, event.target.value)}
                    aria-label={`Texto exibido do valor ${index + 1}`}
                    title={
                      isNumber ? formatVariationValue(item.label, unit) : ""
                    }
                  />
                  <button
                    type="button"
                    onClick={() => removeValue(index)}
                    aria-label={`Remover ${item.label}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <button
            className={styles.primaryButton}
            type="submit"
            disabled={saving}
          >
            {saving
              ? "Salvando..."
              : editing
                ? "Salvar alterações"
                : "Cadastrar variação"}
          </button>
        </form>

        <section className={styles.list}>
          <div className={styles.listHeader}>
            <div>
              <h3>Definições cadastradas</h3>
              <p>Somente definições globais ativas.</p>
            </div>
            <strong>{variations.length}</strong>
          </div>

          {loading && <p>Carregando variações...</p>}
          {!loading && variations.length === 0 && (
            <p className={styles.empty}>Nenhuma variação global cadastrada.</p>
          )}
          {!loading && variations.length > 0 && (
            <div className={styles.cards}>
              {variations.map((variation) => (
                <article className={styles.card} key={variation.id}>
                  <div>
                    <h4>{variation.description || variation.label}</h4>
                    <p className={styles.cardTitle}>
                      Título: {variation.label}
                    </p>
                    <p>{describeVariation(variation)}</p>
                  </div>
                  <div className={styles.cardActions}>
                    <span>{variation.isActive ? "Ativa" : "Inativa"}</span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/admin/v2/variacoes/${variation.id}/editar`)
                      }
                    >
                      Editar
                    </button>
                    {variation.isActive && (
                      <button
                        type="button"
                        onClick={() => void handleDeactivate(variation.id)}
                      >
                        Desativar
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </section>
    </div>
  );
};
